import i18next from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import moment from 'moment';
import { initReactI18next } from 'react-i18next';
import settings from '/@/core/settings';

const configureLocale = async (language: string) => {
    const currentLanguage = language || 'es';

    try {
        await i18next
            .use(initReactI18next)
            .use(
                resourcesToBackend(async (lang: string) => {
                    const translations = await import(`./${lang}/translation.json`);
                    return translations.default;
                })
            )
            .init({
                lng: currentLanguage,
                fallbackLng: false,
                initImmediate: false,
                nsSeparator: false,
                keySeparator: false,
                interpolation: { escapeValue: false },
                returnNull: false,
                returnEmptyString: false,
                load: 'currentOnly',
            });

        await i18next.changeLanguage(currentLanguage);

        moment.locale(currentLanguage);
        settings.changeLanguage(currentLanguage);
    } catch (error) {
        console.error('Error initializing i18next:', error);
    }
};

export default configureLocale;

export const getFirstNotNull = (obj) => {
    for (const key in obj) {
        if (Object.hasOwn(obj, key) && obj[key] !== null) {
            return obj[key];
        }
    }
    return '';
};

export const _ = (...args): string => {
    const activeLanguage = getActiveLanguage();

    if (typeof args[0] === 'object') {
        if (args[0][activeLanguage]) {
            return args[0][activeLanguage];
        }
        return getFirstNotNull(args[0]);
    }

    if (!i18next.isInitialized) {
        return typeof args[0] === 'string' ? args[0] : '';
    }

    const splitted = (i18next.t(...args) || '').split('\n');

    if (splitted.length > 1) {
        return splitted.reduce((result, text, index) => {
            if (index > 0) result.push(<br key={`${text}-${index}`} />);
            result.push(text.trim());
            return result;
        }, []);
    }

    return splitted[0];
};

export const _lazy =
    (...args) =>
    () =>
        _(...args);

const languageChangeCallbacks = [];
export const onLanguageChange = (callback) => {
    languageChangeCallbacks.push(callback);
};

// Create a fallback mechanism for language detection
let fallbackLanguage = 'es';

export const getActiveLanguage = () => {
    const language = i18next.resolvedLanguage || i18next.language || fallbackLanguage;
    return language;
};

export const setActiveLanguage = async (language) => {
    // Update fallback immediately so other functions can use it
    fallbackLanguage = language;

    try {
        // Force load translations for the language if not already loaded
        if (!i18next.hasResourceBundle(language, 'translation')) {
            await i18next.loadLanguages(language);
        }

        settings.changeLanguage(language);
        moment.locale(language);

        await i18next.changeLanguage(language);

        languageChangeCallbacks.forEach((callback) => callback(language));
    } catch (error) {
        console.error('Error changing language:', error);
    }
};
